// src/app/cognito.service.ts (standalone or in a services folder)
import { Injectable } from "@angular/core";
import {
	AuthenticationDetails,
	CognitoUser,
	CognitoUserAttribute,
	CognitoUserPool,
	CognitoUserSession,
	ICognitoUserPoolData,
	ISignUpResult,
} from "amazon-cognito-identity-js";
import { environment } from "@env";

@Injectable({ providedIn: "root" })
export class AuthenticationService {
	private readonly userPool: CognitoUserPool;
	private readonly cognitoUserPoolId: string;
	private readonly cognitoClientAppId: string;
	constructor() {
		this.cognitoUserPoolId = environment.COGNITO_USER_POOL_ID;
		this.cognitoClientAppId = environment.COGNITO_CLIENT_APP_ID;

		const poolData: ICognitoUserPoolData = {
			UserPoolId: this.cognitoUserPoolId,
			ClientId: this.cognitoClientAppId,
		};
		// @ts-ignore
		this.userPool = new CognitoUserPool(poolData);
	}

	signUp(email: string, password: string): Promise<ISignUpResult> {
		//TODO utilize sdk
		return new Promise((resolve, reject) => {
			this.userPool.signUp(
				email,
				password,
				[
					new CognitoUserAttribute({ Name: "email", Value: email }),
					new CognitoUserAttribute({ Name: "name", Value: email }),
				], // user attributes
				[],
				(err, result) => {
					if (err) {
						reject(err);
					} else if (!result) {
						reject(new Error("No result from signUp."));
					} else {
						resolve(result);
					}
				},
			);
		});
	}

	confirmSignUp(email: string, code: string): Promise<"SUCCESS" | undefined> {
		//TODO utilise sdk
		return new Promise((resolve, reject) => {
			const userData = {
				Username: email,
				Pool: this.userPool,
			};
			const cognitoUser = new CognitoUser(userData);

			cognitoUser.confirmRegistration(code, true, (err, result) => {
				if (err) {
					reject(err);
				} else {
					resolve(result);
				}
			});
		});
	}

	getCurrentSession(): Promise<CognitoUserSession | null> {
		const cognitoUser = this.userPool.getCurrentUser();
		if (!cognitoUser) {
			return Promise.resolve(null);
		}

		return new Promise((resolve, reject) => {
			cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
				if (err) {
					reject(err);
				} else {
					resolve(session);
				}
			});
		});
	}

	sendVerificationCode(email: string) {
		return new Promise((resolve, reject) => {
			const userData = {
				Username: email,
				Pool: this.userPool,
			};
			const cognitoUser = new CognitoUser(userData);

			cognitoUser.resendConfirmationCode((err, result) => {
				if (err) {
					console.error("Cognito resendConfirmationCode error:", err);
					reject(err);
				} else {
					console.log("Cognito resendConfirmationCode success:", result);
					resolve(cognitoUser);
				}
			});
		});
	}

	public async refreshAccessToken() {
		const cognitoUser = this.userPool.getCurrentUser();
		const currentSession = await this.getCurrentSession();
		if (currentSession) {
			const refreshToken = currentSession.getRefreshToken();
			if (cognitoUser) {
				cognitoUser.refreshSession(refreshToken, async (err, result) => {
					if (err) {
						console.error("Cognito refreshSession error:", err);
						throw err;
					} else {
						console.log("Cognito refreshSession success:", result);
						return result;
					}
				});
			}
		}
	}
	signOut(): void {
		const cognitoUser = this.userPool.getCurrentUser();
		if (cognitoUser) {
			cognitoUser.signOut();
		}
	}
}
